import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { getModules, saveDisabledModules, type ModuleState } from '../../lib/modulesApi';
import { MODULE_CATALOG } from '../../lib/moduleCatalog';
import { MODULES_KEY } from '../../hooks/useModules';
import { Switch } from '../ui/Switch';

const LABELS = new Map(MODULE_CATALOG.map((m) => [m.key, m.label]));

/**
 * Settings → Module (ADMIN only): switch whole modules of the app on or off
 * for everybody. A switched-off module disappears from the sidebar and its API
 * answers 403, for admins too; turning it back on restores everyone's previous
 * permissions untouched. Modules the instance's subscription does not include
 * (Firmio Console) are shown locked: they can only be enabled by Firmio.
 */
export function ModulesSettings() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: MODULES_KEY, queryFn: getModules });
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const modules = query.data?.modules ?? [];
  const byKey = new Map(modules.map((m) => [m.key, m]));

  const consoleLinked = query.data?.consoleLinked ?? true;

  async function toggle(module: ModuleState, on: boolean) {
    // An opt-in module on an unlinked instance: the switch is the grant itself.
    const grantLocally = Boolean(module.optIn) && !consoleLinked;
    const disabled = modules
      .filter((m) => (m.key === module.key && !grantLocally ? !on : m.disabledLocally))
      .filter((m) => !(grantLocally && m.key === module.key))
      .map((m) => m.key);
    const optIn = grantLocally
      ? modules
          .filter((m) => m.optIn && (m.key === module.key ? on : m.allowedByConsole))
          .map((m) => m.key)
      : undefined;
    setSaving(module.key);
    setError(null);
    try {
      const next = await saveDisabledModules(disabled, optIn);
      qc.setQueryData(MODULES_KEY, next);
      // The matrix the server hands out changed with it.
      await qc.invalidateQueries({ queryKey: ['permissions'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('settings.unknownError'));
    } finally {
      setSaving(null);
    }
  }

  function description(m: ModuleState): string | undefined {
    if (m.core) return t('settings.modules.core');
    if (m.optIn && !consoleLinked && !m.allowedByConsole) return undefined;
    if (m.optIn && !m.allowedByConsole) return t('settings.modules.optInLocked');
    if (!m.allowedByConsole) return t('settings.modules.consoleLocked');
    const parent = m.parent ? byKey.get(m.parent) : undefined;
    if (parent && !parent.core && !parent.enabled) return t('settings.modules.parentOff');
    return undefined;
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-card p-5">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-brand-600/15 p-2 text-brand-600">
          <LayoutGrid className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{t('settings.modules.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('settings.modules.description')}</p>
        </div>
      </div>

      {query.isError && (
        <p className="mt-4 text-sm text-red-600">{t('settings.modules.loadError')}</p>
      )}
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

      {query.data && (
        <div className="mt-4 divide-y divide-gray-100">
          {modules.map((m) => {
            const note = description(m);
            // The switch shows what the admin decided; a locked row (core,
            // not in the subscription, parent off) cannot be changed here.
            const locked = m.core || note !== undefined;
            return (
              <Switch
                key={m.key}
                className={m.parent ? 'pl-8' : undefined}
                checked={m.core || (m.allowedByConsole && !m.disabledLocally)}
                disabled={locked || saving !== null}
                onChange={(next) => void toggle(m, next)}
                label={LABELS.get(m.key) ?? m.key}
                description={
                  note ?? (m.optIn && !consoleLinked ? t('settings.modules.optInLocal') : undefined)
                }
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
